<template>
  <div>
    <div class="grid grid-cols-7 items-center">
      <SelectNative
        v-model="selectedHour"
        :placeholder="$t('components.datepicker.select-hour')"
        :options="hours"
        :disabled="disabled"
        :class="timeFormat === '24h' ? 'col-span-3' : 'col-span-2'"
        :get-display-name="getTimeDisplayName"
      />
      <span class="col-span-1 text-center">:</span>
      <SelectNative
        v-model="selectedMinute"
        :placeholder="$t('components.datepicker.select-minute')"
        :options="minutes"
        :disabled="disabled"
        :class="timeFormat === '24h' ? 'col-span-3' : 'col-span-2'"
        :get-display-name="getTimeDisplayName"
      />
      <SelectNative
        v-if="timeFormat === '12h'"
        v-model="ampm"
        :options="['am', 'pm']"
        :disabled="disabled"
        class="col-span-2"
      />
    </div>
    <div v-if="value && !disabled && canRemoveTime" class="pt-2 text-right">
      <span class="clickable-text" @click="removeTime">
        {{ $t('components.datepicker.remove-time') }}
      </span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { klona } from 'klona';
import { set } from 'lodash';
import type {
  DatepickerSelectedDate,
  DatepickerMode,
  DatepickerTimeFormat,
  DatepickerTime,
} from '@/types';
import { isSameDay } from '@/util/date-utils';

const props = defineProps({
  modelValue: {
    type: Object as PropType<DatepickerTime>,
  },
  timeFormat: {
    type: String as PropType<DatepickerTimeFormat>,
    default: '24h' as DatepickerTimeFormat,
  },
  maxDate: {
    type: Object as PropType<Date>,
  },
  minDate: {
    type: Object as PropType<Date>,
  },
  selectedDate: {
    type: Object as PropType<DatepickerSelectedDate>,
  },
  mode: {
    type: String as PropType<DatepickerMode>,
    default: 'date' as DatepickerMode,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  canRemoveTime: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['update:modelValue']);

const value = computed({
  get: () => props.modelValue,
  set: (newValue) => emit('update:modelValue', newValue),
});

const ampm = ref<'am' | 'pm'>(
  props.timeFormat === '12h' && (value.value?.h ?? 0) < 12 ? 'am' : 'pm'
);

const isMaxDaySelected = computed(
  () =>
    props.maxDate &&
    props.selectedDate?.day &&
    isSameDay(props.selectedDate?.day.date, props.maxDate)
);

const isMinDaySelected = computed(
  () =>
    props.minDate &&
    props.selectedDate?.day &&
    isSameDay(props.selectedDate?.day.date, props.minDate)
);

const hours = computed(() => {
  let length = parseInt(props.timeFormat.replace('h', ''));
  let startIndex = 0;

  if (props.maxDate && isMaxDaySelected.value) {
    length =
      props.maxDate.getHours() <= length
        ? props.maxDate.getHours() + 1
        : length;
  }

  if (props.minDate && isMinDaySelected.value) {
    startIndex =
      props.minDate.getHours() <= length ? props.minDate.getHours() : length;
    length -= startIndex;
  }

  return Array.from(
    { length },
    (_, index) =>
      (props.timeFormat === '12h'
        ? index + 1 + startIndex
        : index + startIndex) + ''
  );
});
const minutes = computed(() => {
  let length = 60;
  let startIndex = 0;
  if (
    props.maxDate &&
    isMaxDaySelected.value &&
    parseInt(selectedHour.value) === props.maxDate.getHours()
  ) {
    length =
      props.maxDate.getMinutes() <= length
        ? props.maxDate.getMinutes() + 1
        : length;
  }

  if (
    props.minDate &&
    isMinDaySelected.value &&
    parseInt(selectedHour.value) === props.minDate.getHours()
  ) {
    startIndex = startIndex + props.minDate.getMinutes();
    length = length - startIndex;
  }

  return Array.from({ length }, (_, index) => index + startIndex + '');
});

const selectedHour = computed({
  get: () => {
    let h = value.value?.h ?? -1;

    if (h >= 0 && props.timeFormat === '12h') {
      h = h % 12;
      h = h || 12; // the hour '0' should be '12'
    }

    return h.toString();
  },
  set: (newValue: string) => {
    let parsedValue = parseInt(newValue);
    if (props.timeFormat === '12h') {
      if (ampm.value === 'pm' && parsedValue < 12) {
        parsedValue += 12;
      } else if (ampm.value === 'am' && parsedValue >= 12) {
        parsedValue -= 12;
      }
    }

    const dateTemp = value.value
      ? klona(value.value)
      : { h: undefined, m: undefined };
    set(dateTemp, ['h'], parsedValue);
    if (dateTemp.m) {
      set(dateTemp, ['m'], dateTemp.m);
      value.value = dateTemp;
    }
    value.value = dateTemp;
  },
});

const selectedMinute = computed({
  get: () => {
    const minute = value.value?.m ?? -1;

    return minute.toString();
  },
  set: (newValue: string) => {
    const parsedValue = parseInt(newValue);
    const dateTemp = value.value
      ? klona(value.value)
      : { h: undefined, m: undefined };
    set(dateTemp, ['m'], parsedValue);
    if (dateTemp.h) {
      set(dateTemp, ['h'], dateTemp.h);
    }
    value.value = dateTemp;
  },
});

function removeTime() {
  let dateWithoutTime = klona(value.value);
  if (!dateWithoutTime) {
    return;
  }
  dateWithoutTime = { h: undefined, m: undefined };
  value.value = dateWithoutTime;
}

const getTimeDisplayName = (option: string): string => {
  if (option.length === 1) {
    return '0' + option;
  }

  return option;
};

watch(ampm, () => {
  if (props.mode === 'date-range') {
    return;
  }
  // Trigger set function of selectedHour again
  const value = selectedHour.value;
  selectedHour.value = value;
});
</script>
